'use client';

import {fetchUploadScreenshot, getCompressedImage} from '@blesk-frontend/api';
import {createId} from '@paralleldrive/cuid2';
import {LucideIcon} from 'lucide-react';
import {AnimatePresence, motion} from 'motion/react';
import {ReactElement, useCallback, useMemo, useState} from 'react';
import {Accept, useDropzone} from 'react-dropzone';

import {cn} from '../../libs/tailwind';
import {Loader} from '../Loader';
import {MenuItem, MenuItemProps} from './MenuItem';

interface MenuItemUploadProps extends Omit<MenuItemProps, 'accept'> {
  accept: Accept;
  onUploaded: (src: string) => void;
  onUploadError?: (error: Error) => void;
}

const prefetchImagePromise = (src: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image'));
  });
};

export const MenuItemUpload = ({
  children,
  accept,
  onUploaded,
  onUploadError,
  ...rest
}: MenuItemUploadProps) => {
  const [progress, setProgress] = useState<null | number>(null);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }

      try {
        setProgress(0);
        const compressedResult = await getCompressedImage(acceptedFiles[0]);
        const uploadedFile = await fetchUploadScreenshot(compressedResult.blob, {
          rects: [],
          filename: [`${createId()}.webp`],
          prefix: 'editor',
          onProgress: setProgress,
        });
        setProgress(99);
        await prefetchImagePromise(uploadedFile.original);
        onUploaded(uploadedFile.original);
        setProgress(100);
      } catch (e) {
        if (e instanceof Error) {
          onUploadError?.(e);
        }
      } finally {
        setProgress(null);
      }
    },
    [onUploaded, onUploadError],
  );

  const {getRootProps, getInputProps} = useDropzone({
    accept,
    onDrop,
  });

  const isUploading = progress !== null;

  const icon = useMemo(
    () => (rest.icon ? isUploading ? <Loader /> : rest.icon : null) as LucideIcon | ReactElement,
    [rest.icon, isUploading],
  );

  return (
    <MenuItem
      {...getRootProps(rest)}
      className={cn(isUploading && 'pointer-events-none text-brand-secondary')}
      icon={icon}
    >
      <input {...getInputProps()} />
      <AnimatePresence>
        {isUploading && (
          <motion.div
            className="absolute inset-0.5 rounded-md bg-brand-secondary/16 transition-width"
            initial={{x: 4, width: 0, opacity: 0}}
            animate={{x: 0, width: `${progress}%`, opacity: 1}}
            exit={{x: -4, width: '100%', opacity: 0}}
          />
        )}
      </AnimatePresence>
      <div className="relative z-10">{isUploading ? `Загрузка файла ${progress}%` : children}</div>
    </MenuItem>
  );
};
